// src/features/system/orchestrator/api/iotPendingApi.ts
// ────────────────────────────────────────────────────────────────────────────
// TIER1 IoT Pending Device API — typed client for BFF /device-ops/iot/* endpoints.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

/** A pending IoT device discovered by the orchestrator device-gate. */
export interface PendingIotDevice {
  device_id: string;
  id_scope: number;
  first_seen: string | null;
  updated_at: string | null;
}

/** Response from GET /device-ops/iot/pending */
export interface PendingIotListResponse {
  items: PendingIotDevice[];
}

/** Response from POST /device-ops/iot/approve */
export interface ApproveIotResponse {
  status: 'approved';
  device_id: string;
  register: unknown;
}

/** Response from POST /device-ops/iot/reject */
export interface RejectIotResponse {
  status: 'rejected';
  device_id: string;
}

// ── API calls ────────────────────────────────────────────────────────────

export const fetchPendingIotDevicesApi = async (): Promise<PendingIotDevice[]> => {
  const res = await bff.get<PendingIotListResponse>('/device-ops/iot/pending');
  return Array.isArray(res?.items) ? res.items : [];
};

export const approveIotDeviceApi = (
  device_id: string,
  label?: string,
): Promise<ApproveIotResponse> =>
  bff.post<ApproveIotResponse>('/device-ops/iot/approve', {
    device_id,
    ...(label ? { label } : {}),
  });

export const rejectIotDeviceApi = (
  device_id: string,
  reason?: string,
): Promise<RejectIotResponse> =>
  bff.post<RejectIotResponse>('/device-ops/iot/reject', {
    device_id,
    ...(reason ? { reason } : {}),
  });

// ── Blacklisted devices ──────────────────────────────────────────────────

/** A blacklisted IoT device (rejected by admin). */
export interface BlacklistedIotDevice {
  device_id: string;
  id_scope: number;
  first_seen: string | null;
  updated_at: string | null;
}

/** Response from GET /device-ops/iot/blacklist */
export interface BlacklistedIotListResponse {
  items: BlacklistedIotDevice[];
}

/** Response from POST /device-ops/iot/unblock */
export interface UnblockIotResponse {
  status: 'unblocked';
  device_id: string;
}

export const fetchBlacklistedIotDevicesApi = async (): Promise<BlacklistedIotDevice[]> => {
  const res = await bff.get<BlacklistedIotListResponse>('/device-ops/iot/blacklist');
  return Array.isArray(res?.items) ? res.items : [];
};

export const unblockIotDeviceApi = (
  device_id: string,
): Promise<UnblockIotResponse> =>
  bff.post<UnblockIotResponse>('/device-ops/iot/unblock', { device_id });
